import {useEffect, useState} from 'react'
import {PD_ACCORDION_STATE_EVENT, PD_ACCORDION_MODULE_ATTR} from '_uiComponents'

interface AccordionTriggerState {
    /** True when `ctaUrl` targets a `32_accordion` module present in the DOM (so a chevron should show). */
    isAccordionTrigger: boolean
    /** Current open state of the targeted accordion (drives chevron direction). */
    isOpen: boolean
}

/**
 * @name useAccordionTrigger
 *
 * @description
 * For a Page Designer CTA whose `cta_url` may be the `moduleId` of a `32_accordion` module, tells the
 * CTA whether it targets an accordion (to decide if a chevron should be shown) and tracks that
 * accordion's open/closed state (to flip the chevron). Fully decoupled: it detects the accordion by
 * its DOM marker and follows state via the accordion's broadcast state event.
 *
 * @param {string} [ctaUrl] - The CTA link; when it equals an accordion `moduleId`, this becomes the trigger.
 * @returns {AccordionTriggerState} `{isAccordionTrigger, isOpen}`.
 */
export const useAccordionTrigger = (ctaUrl?: string): AccordionTriggerState => {
    const moduleId = ctaUrl?.trim()
    const [isAccordionTrigger, setIsAccordionTrigger] = useState(false)
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (!moduleId || typeof document === 'undefined') {
            setIsAccordionTrigger(false)
            return
        }

        // Detect a matching accordion in the DOM (client-side; SSR renders no chevron until hydration).
        setIsAccordionTrigger(Boolean(document.querySelector(`[${PD_ACCORDION_MODULE_ATTR}="${CSS.escape(moduleId)}"]`)))

        const onState = (event: Event) => {
            const detail = (event as CustomEvent<{moduleId?: string; open?: boolean}>).detail
            if (!detail || detail.moduleId !== moduleId) return
            // Receiving a state event also confirms the accordion exists.
            setIsAccordionTrigger(true)
            setIsOpen(Boolean(detail.open))
        }

        document.addEventListener(PD_ACCORDION_STATE_EVENT, onState)
        return () => document.removeEventListener(PD_ACCORDION_STATE_EVENT, onState)
    }, [moduleId])

    return {isAccordionTrigger, isOpen}
}
